import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { useGetStaticAssetUrl } from '@change-corgi/core/react/assets';
import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Image } from '@change-corgi/design-system-legacy/components/media';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';

import { AccessibleFileInputWrapper } from './AccessibleFileInputWrapperSunrise';

type Props = {
	disabled?: boolean;
	onButtonClick?: () => void;
	uppy: Uppy<Meta, Body>;
	fileInputId?: string;
};
export const ImageUploader: React.FC<Props> = ({
	disabled = false,
	onButtonClick,
	uppy,
	fileInputId = 'file-input-id',
}) => {
	const getStaticAssetUrl = useGetStaticAssetUrl();
	const DEFAULT_MEDIA_UPLOAD_IMAGE = getStaticAssetUrl('/images/image-placeholder-drop.svg');
	const { translate } = useI18n();
	const uploadAPhotoText = { strings: { chooseFiles: translate('corgi.sap.button.upload') } };

	return (
		<Box className="image-uploader" sx={{ aspectRatio: '16/9' }}>
			<Box
				variant="bordered"
				sx={{
					borderStyle: 'dashed',
					height: '100%',
					borderColor: 'sunrise.semantic.stroke.strong',
				}}
			>
				<Flex
					className="media-upload-content-wrapper"
					sx={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'column', height: '100%' }}
				>
					<Box mt={64} mb={32} sx={{ lineHeight: 0, display: ['none', 'block'] }}>
						<Image
							data-testid="drop-target"
							src={DEFAULT_MEDIA_UPLOAD_IMAGE}
							width="115"
							alt={translate('corgi.sap.media.a11y.uploader.image_alt_text')}
						/>
					</Box>
					<Box mb={64} id="sap-media-view">
						<VisuallyHidden as="span">
							<Translate value="corgi.sap.media.a11y.uploader.accessible_hidden_div" />
						</VisuallyHidden>
						{!disabled ? (
							<AccessibleFileInputWrapper
								uploadButtonText={uploadAPhotoText.strings.chooseFiles}
								uppy={uppy}
								fileInputId={fileInputId}
								onButtonClick={onButtonClick}
							/>
						) : (
							<Button disabled={disabled} data-qa="media-images-upload">
								{uploadAPhotoText.strings.chooseFiles}
							</Button>
						)}
					</Box>
				</Flex>
			</Box>
		</Box>
	);
};

ImageUploader.displayName = 'ImageUploader';
